import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { tourService } from '../services/tourService';
import { Tour } from '../types';
import TourCard from '../components/Tours/TourCard';
import {
    HeartIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline';

const Wishlist: React.FC = () => {
    const { user, isAuthenticated } = useAuth();
    const { actualTheme } = useTheme();
    const navigate = useNavigate();
    const [favoriteTours, setFavoriteTours] = useState<Tour[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        const fetchFavorites = async () => {
            try {
                // TODO: Implement API call to fetch user's favorite tours
                // For now, we'll get all tours and filter by favorites stored in localStorage
                const allTours = await tourService.getAllTours();
                const favoriteIds = JSON.parse(localStorage.getItem(`favorites_${user?.id}`) || '[]');
                const favorites = allTours.filter(tour => favoriteIds.includes(tour.id));
                setFavoriteTours(favorites);
            } catch (error) {
                console.error('Error loading favorites:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchFavorites();
    }, [isAuthenticated, navigate, user]);

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
            }`}>
            {/* Header */}
            <div className={`backdrop-blur-md shadow-modern sticky top-0 z-50 transition-all duration-300 ${actualTheme === 'dark'
                    ? 'bg-gradient-to-r from-slate-800/95 to-slate-700/95 border-b border-slate-600/30'
                    : 'bg-white/95 border-b border-neutral-200/50'
                }`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <button
                            onClick={() => navigate('/')}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-modern text-sm font-medium border transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                                    ? 'text-slate-300 border-slate-600/30 hover:text-slate-200 hover:border-slate-500/50'
                                    : 'text-primary-600 border-primary-200 hover:text-primary-700 hover:border-primary-300'
                                }`}
                        >
                            <ArrowLeftIcon className="w-4 h-4" />
                            <span>Back to Home</span>
                        </button>
                        <h1 className={`text-2xl font-bold transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>My Wishlist</h1>
                        <div className="w-24"></div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                ) : favoriteTours.length === 0 ? (
                    <div className={`text-center py-12 rounded-2xl shadow-modern ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
                        }`}>
                        <HeartIcon className={`w-16 h-16 mx-auto mb-4 ${actualTheme === 'dark' ? 'text-slate-600' : 'text-gray-400'
                            }`} />
                        <h3 className={`text-xl font-semibold mb-2 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                            }`}>No Favorites Yet</h3>
                        <p className={`mb-6 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'
                            }`}>Start adding tours to your wishlist by clicking the heart icon!</p>
                        <button
                            onClick={() => navigate('/tours')}
                            className="px-6 py-3 rounded-modern font-medium transition-all duration-300 hover-magnify-sm bg-primary-600 hover:bg-primary-700 text-white"
                        >
                            Browse Tours
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={`mb-6 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                            }`}>
                            <p className="text-lg">
                                You have <span className="font-bold text-primary-600">{favoriteTours.length}</span> tour{favoriteTours.length !== 1 ? 's' : ''} in your wishlist
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {favoriteTours.map((tour) => (
                                <TourCard key={tour.id} tour={tour} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Wishlist;
